/**
 * Layer-panel feedback for edits made by agents and add-ons (ADR-009 §6.1):
 * rows changed by the latest such transaction are highlighted for about two
 * seconds, and a layer it created is scrolled into view. The user's own
 * edits never trigger it, and nothing here moves focus or selection.
 */
import { useEffect, useState } from 'react';
import type { ActorRef, LayerId } from '@maipo/agent-protocol';
import type { HistoryEvent } from '../core/commands';
import { walkLayers } from '../core/model';
import type { DocumentSession } from '../core/session';
import { TransactionCommand } from '../semantic/transaction';

export const HIGHLIGHT_MS = 2_000;

export interface ExternalChange {
  revision: number;
  actor: Extract<ActorRef, { kind: 'agent' } | { kind: 'addon' }>;
  /** Layers the change wrote or created that still exist, in no particular order. */
  layers: LayerId[];
  /** Layers that did not exist before the change. */
  created: LayerId[];
}

function layerIds(session: DocumentSession): Set<LayerId> {
  return new Set(walkLayers(session.document.root).map((layer) => layer.id));
}

/**
 * What an agent or add-on commit (or redo) changed, from the ledger entry for
 * `event` and the layer IDs that existed before it; null for anyone else's
 * edits and for undo. Transactions report the layers they wrote or created
 * (not the parents a structural change only read); native commands committed
 * by an add-on report their damaged layers.
 */
export function externalChangeOf(session: DocumentSession, event: HistoryEvent, before: ReadonlySet<LayerId>): ExternalChange | null {
  if (event.kind !== 'commit' && event.kind !== 'redo') return null;
  const entry = session.ledger.latest();
  if (!entry || entry.revision !== event.revision) return null;
  const actor = entry.actor;
  if (actor.kind !== 'agent' && actor.kind !== 'addon') return null;
  const now = layerIds(session);
  const command = event.command;
  const touched = command instanceof TransactionCommand
    ? [...command.meta.footprint.writes, ...Object.values(command.meta.aliases), ...command.meta.journal.map((change) => change.layer)]
    : entry.footprint.layers;
  const created = [...now].filter((id) => !before.has(id));
  const layers = [...new Set([...touched, ...created])].filter((id) => id !== 'root' && now.has(id));
  return layers.length ? { revision: event.revision, actor, layers, created } : null;
}

/**
 * The latest external change in `session`, cleared after HIGHLIGHT_MS.
 * Evaluated one microtask after the history event, so a dispatched
 * transaction's journal (filled right after it executes) is complete.
 */
export function useExternalChangeHighlight(session: DocumentSession): ExternalChange | null {
  const [change, setChange] = useState<ExternalChange | null>(null);
  useEffect(() => {
    let known = layerIds(session);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let active = true;
    setChange(null);
    const unsubscribe = session.subscribe((event) => {
      if (event.kind === 'preview' || event.kind === 'cancel') return;
      const before = known;
      queueMicrotask(() => {
        if (!active) return;
        known = layerIds(session);
        const next = externalChangeOf(session, event, before);
        if (!next) return;
        setChange(next);
        if (timer !== undefined) clearTimeout(timer);
        timer = setTimeout(() => { if (active) setChange((current) => current === next ? null : current); }, HIGHLIGHT_MS);
      });
    });
    return () => {
      active = false;
      unsubscribe();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [session]);
  return change;
}
